import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

function syllables(word: string): number {
  word = word.toLowerCase().replace(/[^a-z]/g, '');
  if (!word) return 0;
  if (word.length <= 3) return 1;
  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
  word = word.replace(/^y/, '');
  const matches = word.match(/[aeiouy]{1,2}/g);
  return matches ? matches.length : 1;
}

function score(text: string) {
  const words = text.trim().split(/\s+/).filter(Boolean);
  const sentences = text.trim().split(/[.!?]+(?:\s|$)/).filter((s) => s.trim().length > 0);
  const totalSyll = words.reduce((s, w) => s + syllables(w), 0);
  const w = words.length;
  const s = Math.max(1, sentences.length);
  const sy = totalSyll;
  const flesch = w ? 206.835 - 1.015 * (w / s) - 84.6 * (sy / w) : 0;
  const gradeFK = w ? 0.39 * (w / s) + 11.8 * (sy / w) - 15.59 : 0;
  return {
    words: w,
    sentences: s,
    syllables: sy,
    avgWordsPerSentence: w / s,
    avgSyllablesPerWord: sy / Math.max(1, w),
    flesch: Math.round(flesch * 10) / 10,
    grade: Math.round(gradeFK * 10) / 10,
  };
}

function fleschLabel(s: number): { label: string; tone: 'success' | 'warning' | 'danger' } {
  if (s >= 90) return { label: 'Very easy', tone: 'success' };
  if (s >= 70) return { label: 'Fairly easy', tone: 'success' };
  if (s >= 60) return { label: 'Standard', tone: 'success' };
  if (s >= 50) return { label: 'Fairly difficult', tone: 'warning' };
  if (s >= 30) return { label: 'Difficult', tone: 'warning' };
  return { label: 'Very difficult', tone: 'danger' };
}

export function ReadabilityChecker() {
  const [text, setText] = useState(
    'Plain language is more inclusive. Short sentences and common words help everyone — not only newcomers — to read faster and remember more.',
  );
  const stats = useMemo(() => score(text), [text]);
  const flesch = fleschLabel(stats.flesch);

  return (
    <div>
      <ToolHeader
        toolId="readability"
        title="Readability checker"
        description="Flesch-Kincaid scores plus structural stats. Edit live to see the score adjust."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            Text
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[360px] !bg-transparent focus-visible:!ring-0"
          />
        </Card>

        <div className="space-y-3">
          <Card>
            <div className="flex items-center justify-between mb-2">
              <div className="text-2xs uppercase tracking-wider text-fg-subtle">
                Flesch reading ease
              </div>
              <Badge variant={flesch.tone}>{flesch.label}</Badge>
            </div>
            <div className="text-5xl font-mono font-semibold tabular-nums">{stats.flesch}</div>
            <div className="text-xs text-fg-muted mt-1">0 (very hard) → 100 (very easy)</div>
          </Card>
          <Card>
            <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-1">
              Flesch-Kincaid grade
            </div>
            <div className="text-3xl font-mono font-semibold tabular-nums">{stats.grade}</div>
            <div className="text-xs text-fg-muted mt-1">Approximate US school grade</div>
          </Card>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Words" value={stats.words} />
            <Stat label="Sentences" value={stats.sentences} />
            <Stat label="Syllables" value={stats.syllables} />
            <Stat
              label="Avg words / sentence"
              value={stats.avgWordsPerSentence.toFixed(1)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <Card className="!p-3">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="text-sm font-mono mt-0.5 tabular-nums">{value}</div>
    </Card>
  );
}

export default ReadabilityChecker;
